"use client";
import type { DeviceState } from "../../types/DeviceStates";
import { getDeviceStateLabel, isActiveState } from "../../types/DeviceStates";
import { Badge, type BadgeProps } from "../Badge";
import { Icon, type IconName } from "../Icon";
import { Skeleton } from "../Skeleton";

export interface DeviceStateBadgeProps extends Omit<BadgeProps, "variant" | "children"> {
  /** The device state to display */
  state: DeviceState;
  /** Custom label override (defaults to auto-capitalized state name) */
  label?: string;
  /** Optional icon to display before the label */
  icon?: IconName;
  /** Loading state */
  isLoading?: boolean;
  /** Fixed width for column alignment (useful in tables) */
  fixedWidth?: boolean;
}

/**
 * DeviceStateBadge
 *
 * A specialized badge component for displaying device states with smart defaults.
 * Auto-enables pulsing dot for active states (charging, discharging, heat, cool, on).
 * Uses semantic device state colors from the theme.
 *
 * @example
 * ```tsx
 * <DeviceStateBadge state="charging" />
 * <DeviceStateBadge state="heat" label="Heating Mode" />
 * <DeviceStateBadge state="on" icon="Lightning" />
 * ```
 */
export function DeviceStateBadge({
  state,
  label,
  icon,
  isLoading,
  size = "sm",
  shape = "pill",
  dot,
  dotPulse,
  fixedWidth,
  className,
  ...badgeProps
}: DeviceStateBadgeProps) {
  if (isLoading) {
    return <Skeleton className="inline-flex h-6 w-20" />;
  }

  const displayLabel = label ?? getDeviceStateLabel(state);
  const isActive = isActiveState(state);

  // Auto-enable dot for active states unless explicitly overridden
  const shouldShowDot = dot ?? isActive;
  const shouldPulse = dotPulse ?? isActive;

  return (
    <Badge
      variant={state}
      size={size}
      shape={shape}
      dot={shouldShowDot}
      dotPulse={shouldPulse}
      className={fixedWidth ? `w-28 justify-center ${className || ""}` : className}
      {...badgeProps}
    >
      {icon && <Icon name={icon} size={size === "sm" ? 12 : size === "md" ? 14 : 16} />}
      {displayLabel}
    </Badge>
  );
}
